/*
 * The daily figures of the model calls a reader may ask for, apart from the
 * code that holds them to it. `pro-offer.ts` draws its table from them and
 * imports nothing a browser cannot run, so neither does this.
 */

/** A counter a model allowance keeps on the day rows, one per allowance. */
export type AllowanceCounter =
  | "translated"
  | "recorded"
  | "judged"
  | "reported"
  | "spoken"
  | "named"
  | "drawn";

/**
 * How many model calls of one kind a reader may make a day, by membership,
 * and how many free readers may make between them.
 *
 * Hugh set the translation figures and the recording figures on 2026-10-24.
 * The shared figure is a cost guard rather than an offer, and
 * nobody is told it. docs/own-liju.md under "The daily allowances" has the
 * reasoning.
 */
export interface DailyAllowanceRule {
  /** The counter on the day rows. */
  readonly counter: AllowanceCounter;

  /** A Pro 专业 member's daily figure. */
  readonly pro: number;

  /** A free reader's daily figure, a taster of what Pro gives. */
  readonly free: number;

  /** What every free reader together may use on one UTC day. */
  readonly freeShared: number;
}

/**
 * English translations of a reader's own 例句.
 *
 * A thousand free translations a day is under four US cents at the rates the
 * bench measured, so the shared cap is there for a script making accounts,
 * not for the bill of an ordinary day.
 */
export const translationAllowance: DailyAllowanceRule = {
  counter: "translated",
  pro: 50,
  free: 3,
  freeShared: 1000,
};

/**
 * Recordings of a reader's own 例句, in the voice they choose.
 *
 * BytePlus charges $30 a million characters and a sentence holds at most 80,
 * so 500 free recordings a day is at most $1.20. Hugh set the shared figure on
 * 2026-10-24.
 */
export const recordingAllowance: DailyAllowanceRule = {
  counter: "recorded",
  pro: 20,
  free: 2,
  freeShared: 500,
};

/**
 * Orders of a saved 例句's pieces put to a model, on a word-order card.
 *
 * A call is made only for an order nobody has judged before, and its verdict
 * is kept for every reader after, so an ordinary reader asks for a few a day.
 * A call costs about a hundredth of a US cent at Qwen3 235B's rates. The
 * figures are a guard against a script posting order after order, which the
 * answer route would otherwise turn into a call each. A reader past theirs is
 * marked as before any model was asked (`src/user/word-order-judging.ts`).
 */
export const judgedOrderAllowance: DailyAllowanceRule = {
  counter: "judged",
  pro: 200,
  free: 20,
  freeShared: 2000,
};

/**
 * Reports on marked 复习 cards.
 *
 * Not a model allowance, though it is held the same way. A report can put one
 * word-order verdict to a second model, at most once per verdict, and posts a
 * line to Slack every time, so a script reporting card after card would fill
 * the channel. An honest reader reports a card now and then. A reader past
 * theirs is told to try again tomorrow, and nothing is kept
 * (`src/user/fuxi-reporting.ts`).
 */
export const reportAllowance: DailyAllowanceRule = {
  counter: "reported",
  pro: 30,
  free: 10,
  freeShared: 500,
};

/**
 * Words said aloud on a speech card, at 复习 or in a List Exercise.
 *
 * Every attempt is a call to BytePlus, which bills by the second. At its
 * listed $1.00 an hour (not yet confirmed for Seed ASR) and about a second and
 * a half a word, an attempt is about $0.00045 with the Lambda, so 200 a day
 * used in full is $2.70 a month and a free reader's 5 is $0.07. A visitor has
 * none, there being no account to count against.
 *
 * **Voice is Pro, with a free taster.** Hugh set the free figure to 5 on
 * 2026-10-28 (CB-85), the 发音 taster's, and a free reader past it is offered
 * Pro beside the keyboard. The shared figure bounds every free account
 * together, at most about $0.45 a day.
 *
 * Separate from 发音's attempts (`./pronunciation-allowance.ts`), because
 * voice and pronunciation are two features with two offers. A reader past
 * theirs answers on the keyboard and the sitting carries on. A typed answer
 * never counts. docs/voice.md has the rest.
 */
export const spokenAllowance: DailyAllowanceRule = {
  counter: "spoken",
  pro: 200,
  free: 5,
  freeShared: 1000,
};

/**
 * Birthday GIF names put to a model (#1032).
 *
 * Only a name that neither the committed table nor a kept answer settles
 * reaches the model, and every answer is kept for the next learner, so an
 * ordinary learner asks for one or two. A call is a few hundred tokens at
 * Qwen3 235B's rates, about a fiftieth of a US cent. The issue offers the
 * names to every signed-in learner alike, so Pro and free have one figure,
 * and the shared figure bounds a script making accounts to about 40 US cents
 * a day.
 */
export const birthdayNameAllowance: DailyAllowanceRule = {
  counter: "named",
  pro: 10,
  free: 10,
  freeShared: 2000,
};

/**
 * Custom birthday GIFs drawn (#1032).
 *
 * A drawing is about a second and a half of the handler's whole core, so
 * well under a hundredth of a US cent, and the same name, writing and gag a
 * second time is read back from the bucket and takes nothing. The figure is
 * a guard on the handler's time more than on money, and generous for a
 * learner sending wishes to a few friends.
 */
export const birthdayGifAllowance: DailyAllowanceRule = {
  counter: "drawn",
  pro: 20,
  free: 20,
  freeShared: 4000,
};
